/**
 * Companies API endpoints
 * 
 * All company-related API calls go through this module.
 * 
 * Constitution Section 19.4: API Response Validation (Zod Mandatory)
 */

import { apiClient } from '../client';
import { CompaniesArraySchema, CompanySchema, type Company } from '@/schemas/company';

// Re-export Company type for convenience
export type { Company };

/**
 * Get all companies for the authenticated user
 * 
 * Uses /users/me/companies endpoint which returns an array directly.
 * Validates response with Zod schema.
 */
export async function getCompanies(): Promise<Company[]> {
  const response = await apiClient.get<unknown>('/users/me/companies');
  
  // Zod parse at API boundary (Section 19.4)
  const companies = CompaniesArraySchema.parse(response.data);
  
  return companies;
}

/**
 * Get a single company by ID
 * 
 * Validates response with Zod schema.
 */
export async function getCompany(companyId: number): Promise<Company> {
  const response = await apiClient.get<unknown>(`/companies/${companyId}`);
  
  // Zod parse at API boundary (Section 19.4)
  const company = CompanySchema.parse(response.data);
  
  return company;
}

/**
 * Create a new company
 */
export async function createCompany(data: Partial<Company>): Promise<Company> {
  const response = await apiClient.post<Company>('/companies/', data);
  return response.data;
}

/**
 * Update a company
 */
export async function updateCompany(companyId: number, data: Partial<Company>): Promise<Company> {
  const response = await apiClient.put<Company>(`/companies/${companyId}`, data);
  return response.data;
}

/**
 * Delete a company
 */
export async function deleteCompany(companyId: number): Promise<void> {
  await apiClient.delete(`/companies/${companyId}`);
}
